
import React, { useEffect, useState } from 'react';
import { TrophyIcon, ArrowRightIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { getMyCompetitions } from '../../api/userApi'; 
import { useNavigate } from 'react-router-dom';

const MyParticipation = () => {
    const navigate = useNavigate();
    const [participations, setParticipations] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const data = await getMyCompetitions();
                setParticipations(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Fetch error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Logic: Filter out internships
    const competitionsOnly = participations.filter(
        (item) => item.type?.toLowerCase() !== 'internship'
    );

    // Date Formatter Helper
    const formatDate = (dateString) => {
        if (!dateString) return "TBD";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
        }).toUpperCase();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[#050505] flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-indigo-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] text-white p-6 lg:p-10">
            {/* Header Section */}
            <header className="mb-12">
                <h1 className="text-5xl md:text-6xl font-black tracking-tighter italic uppercase text-white">
                    My <span className="text-indigo-600">Arena</span>
                </h1>
                <p className="text-gray-500 text-[10px] font-black uppercase tracking-[0.3em] mt-2">
                    Active Competitions & Challenges
                </p>
            </header>

            {/* Main Table Section */}
            <div className="bg-[#0a0a0a] border border-white/5 rounded-[2rem] overflow-hidden shadow-2xl mb-10">
                <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-white/[0.01]">
                    <h2 className="text-sm font-black text-white italic uppercase tracking-widest flex items-center gap-3">
                        <TrophyIcon className="h-4 w-4 text-indigo-500" />
                        List Of Competitions
                    </h2>
                    <button 
                        onClick={() => navigate('/dashboard/competitions')} 
                        className="text-indigo-400 hover:text-white text-[10px] font-black flex items-center gap-2 uppercase tracking-widest italic transition-all group"
                    >
                        Explore <ArrowRightIcon className="h-3 w-3 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>

                <div className="overflow-x-auto">
                    {competitionsOnly.length > 0 ? (
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="text-gray-600 text-[9px] font-black uppercase tracking-[0.2em] border-b border-white/5">
                                    <th className="px-6 py-4 text-center italic">#</th>
                                    <th className="px-8 py-4 italic text-indigo-400">Competition</th>
                                    <th className="px-8 py-4 italic">Schedule</th>
                                    <th className="px-8 py-4 italic">Status</th>
                                    <th className="px-8 py-4 text-right italic">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {competitionsOnly.map((comp, index) => (
                                    <tr key={comp.id} className="hover:bg-white/[0.02] transition-colors group">
                                        {/* Numbering Column */}
                                        <td className="px-6 py-6 text-center">
                                            <span className="text-[10px] font-black text-gray-700 group-hover:text-indigo-500 transition-colors">
                                                {String(index + 1).padStart(2, '0')}
                                            </span>
                                        </td>

                                        {/* Competition Column */}
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-4">
                                                <div className="h-8 w-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
                                                    <TrophyIcon className="h-4 w-4 text-indigo-500" />
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black uppercase italic tracking-tight text-white leading-none">
                                                        {comp.title}
                                                    </p>
                                                    <p className="text-[9px] text-gray-500 uppercase mt-1 font-bold">
                                                        {comp.type || 'Standard Arena'}
                                                    </p>
                                                </div>
                                            </div>
                                        </td>

                                        {/* Schedule Column */}
                                        <td className="px-8 py-6">
                                            <div className="flex flex-col">
                                                <span className="text-[10px] font-black text-gray-300">
                                                    {formatDate(comp.start_date)} — {formatDate(comp.end_date)}
                                                </span>
                                                <span className="text-[8px] text-gray-600 uppercase font-black tracking-widest">Timeline</span>
                                            </div>
                                        </td>

                                        {/* Status Column */}
                                        <td className="px-8 py-6">
                                            <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-md border ${
                                                comp.payment_status === 'paid' 
                                                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' 
                                                    : 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20'
                                            }`}>
                                                {comp.payment_status}
                                            </span>
                                        </td>

                                        {/* Action Column */}
                                        <td className="px-8 py-6 text-right">
                                            <button 
                                                onClick={() => navigate(`/dashboard/competition-hub/${comp.id}`)}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-[9px] font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-600/20 active:scale-95"
                                            >
                                                Start <ArrowRightIcon className="h-3 w-3" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="p-20 text-center">
                            <SparklesIcon className="h-10 w-10 text-white/5 mx-auto mb-4" />
                            <p className="text-gray-600 font-black italic uppercase tracking-widest text-[10px]">
                                No active arenas found
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MyParticipation;